import { useMutation } from '@apollo/client';
import {
  useStripe,
  useElements,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import type { StripeError } from '@stripe/stripe-js';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { SELECTED_PRODUCT_COOKIE_NAME } from 'src/config/constants';
import Routes from 'src/config/routes';
import {
  PurchaseAndActivateDocument,
  type ListingProduct,
} from 'src/generated/graphql';
import { ConsoleErrorId, consoleErrorWrapper } from 'src/utils';
import { firePaymentSuccessEvent } from 'src/utils/analytics';
import { removeCookie } from 'src/utils/cookies';

import { getError } from '../utils';

export const usePaymentSubmission = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [purchaseProduct, purchaseProductResult] = useMutation(
    PurchaseAndActivateDocument,
  );
  const [stripeError, setStripeError] = useState<StripeError | null>();

  const pay = async (options: {
    clientId: string;
    listingId: string | null;
    selectedProduct: ListingProduct;
    sourceProducts: ListingProduct[];
  }) => {
    const cardNumber = elements?.getElement(CardNumberElement);

    if (!stripe || !elements || !cardNumber) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      consoleErrorWrapper(ConsoleErrorId.StripeUninitialised);
      return;
    }

    // Reset error state
    setStripeError(null);

    // This will populate result.error if the payment information is invalid
    const result = await stripe.createPaymentMethod({
      type: 'card',
      card: cardNumber,
    });

    if (result.error) {
      setStripeError(result.error);
      return;
    }

    try {
      await purchaseProduct({
        variables: {
          input: {
            clientId: options.clientId,
            paymentMethodId: result.paymentMethod.id,
            productId: options.selectedProduct.productToken,
            listingId: options.listingId,
          },
        },
      });
    } catch {
      // Let purchaseProductResult.error handle rendering our error message
      return;
    }

    removeCookie(SELECTED_PRODUCT_COOKIE_NAME);
    firePaymentSuccessEvent(options.sourceProducts);
    dispatch({ type: 'REQUEST_PAYMENT_SUCCESS' });

    const newSearchParams = new URLSearchParams();
    options.sourceProducts.forEach((product) =>
      newSearchParams.append('productToken', product.productToken),
    );
    if (options.listingId) {
      newSearchParams.set('listingId', options.listingId);
    }

    navigate(`${Routes.PAYMENT_SUCCESS}?${newSearchParams.toString()}`);
  };

  const error = getError(stripeError, purchaseProductResult);

  return { pay, loading: purchaseProductResult.loading, error };
};
